'use client';

import { type AxiosInstance } from 'axios';
import {
  createContext,
  useContext,
  type Dispatch,
  type SetStateAction,
} from 'react';

import {
  AnimalSpeciesBreedApi,
  AuthorisedRepresentativeApi,
  BillableEventApi,
  DocumentApi,
  EntityApi,
  EntityDetailApi,
  EntityDetailConfigApi,
  EntityDetailEnhancerApi,
  EntityDetailValueApi,
  EntityTransferApi,
  FormActionAssociationApi,
  ImportBatchApi,
  FormApi,
  NoteApi,
  OrganisationApi,
  OrganisationDto,
  OrganisationSettingApi,
  RoleUserOrgAssociationApi,
  RoleUserOrgAssociationDto,
  ScreenApi,
  TimelineApi,
  UserConfigApi,
  ValidationApi as ValidationRuleApi,
  RuleExecutionApi,
  RuleConfigurationApi,
  RoleApi,
  SolrSyncApi,
} from '@/apiClient';
import { UserApi, UserProfileApi, type UserProfileDto } from '@/authApiClient';

interface CoreApiClients {
  entityApi?: EntityApi;
  entityDetailApi?: EntityDetailApi;
  entityDetailConfigApi?: EntityDetailConfigApi;
  entityDetailEnhancerApi?: EntityDetailEnhancerApi;
  entityDetailValueApi?: EntityDetailValueApi;
  entityTransferApi?: EntityTransferApi;
  animalSpeciesBreedApi?: AnimalSpeciesBreedApi;
  animalDetailApi?: EntityDetailApi;
  documentApi?: DocumentApi;
  noteApi?: NoteApi;
  timelineApi?: TimelineApi;
  importBatchApi?: ImportBatchApi;
  screenApi?: ScreenApi;
}

interface FormApiClients {
  formApi?: FormApi;
  formActionAssociationApi?: FormActionAssociationApi;
}

interface OrgApiClients {
  organisationApi?: OrganisationApi;
  organisationSettingApi?: OrganisationSettingApi;
  roleUserOrgAssociationApi?: RoleUserOrgAssociationApi;
  roleApi?: RoleApi;
  solrSyncApi?: SolrSyncApi;
}

interface AuthApiClients {
  userApi?: UserApi;
  userProfileApi?: UserProfileApi;
  authorisedRepresentativeApi?: AuthorisedRepresentativeApi;
}

interface RuleApiClients {
  validationRuleApi?: ValidationRuleApi;
  ruleExecutionApi?: RuleExecutionApi;
  ruleConfigurationApi?: RuleConfigurationApi;
}

interface FinanceApiClients {
  billableEventApi?: BillableEventApi;
  userConfigApi?: UserConfigApi;
}

type ApiClients = CoreApiClients &
  FormApiClients &
  OrgApiClients &
  AuthApiClients &
  RuleApiClients &
  FinanceApiClients;

interface UserData {
  userId?: number;
  userGuid?: string;
  userEmail?: string;
  isABRIStaff?: boolean;
  entitlements?: string[];
  userProfile?: UserProfileDto;
  /** True when the post-login profile fetch (PostLoginFlow) has failed and
   *  is not going to resolve `userProfile`. Lets the UI (e.g. the header
   *  avatar) settle into a degraded state instead of spinning forever. */
  userProfileError?: boolean;
  reloadUserProfile?: () => void;
}

interface OrganisationState {
  selectedOrganisationId?: number;
  // Accepts `undefined` so consumers can clear the selection (e.g. on logout).
  setSelectedOrganisationId?: (id: number | undefined) => void;
  selectedOrganisation?: OrganisationDto;
  // Accepts `undefined` so consumers can clear the selection (e.g. on logout).
  setSelectedOrganisation?: (organisation: OrganisationDto | undefined) => void;
  userOrganisations?: RoleUserOrgAssociationDto[];
  // Accepts `undefined` so consumers can reset to the "not fetched yet"
  // sentinel (e.g. on logout), distinct from `[]` ("no memberships").
  setUserOrganisations?: (
    organisations: RoleUserOrgAssociationDto[] | undefined,
  ) => void;
}

export interface PrivateContextData
  extends ApiClients, UserData, OrganisationState {
  axiosInstance?: AxiosInstance;
  loginFlowComplete?: boolean;
  showSplashScreen?: boolean;
  setShowSplashScreen?: (show: boolean) => void;
  /** Writer for the whole context. Used by `Private` to fold session +
   *  user-profile data into the lifted state once auth resolves. Exposed
   *  via context so the state can live above `Shell`, which lets the
   *  navbar's `UserAvatar` keep working without Shell being remounted
   *  when auth transitions LOADING → AUTHENTICATED. */
  setContextData?: Dispatch<SetStateAction<PrivateContextData>>;
}

const PrivateContext = createContext<PrivateContextData>({});

export const usePrivate = () => {
  return useContext(PrivateContext);
};

export const PrivateContextProvider = PrivateContext.Provider;
